'use client'

import { PRODUCT_IMAGE_SIZE_THUMBNAIL } from '@/feature/product/services/product.constants'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { isItemDefined } from '@/lib/utils/array'
import { Button } from '@/ui/buttons/button/Button.client'
import { QuantityPicker } from '@/ui/form/inputs/quantity-picker/QuantityPicker.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Link } from '@/ui/links/link/Link.client'
import { Loader } from '@/ui/loaders/loader/Loader'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { StatusLine } from '@/ui/status-line/StatusLine'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import type { Maybe, ProductRow_ProductFragment, Tag } from '@gql-generated/graphql'
import {
  ClickAndCollectStatus,
  GetLinkedProductsConfigDocument,
  ShippingDelay,
  Unit,
} from '@gql-generated/graphql'
import { memo, useCallback, useMemo, useTransition } from 'react'
import { getSafeProductName } from '../../services/product.filtering'
import { createDisplayProductScreenIntent } from '../../services/product.intents'
import { toProductPage } from '../../services/product.routing'
import { getAvailabilityIssue } from '../../utils/productBuyBox'
import { getProductDisplayPrice } from '../../utils/productDisplayPrice'
import { ProductRowLinkedProducts } from '../product-row-linked-products/ProductRowLinkedProducts.client'
import { ProductTag } from '../product-tag/ProductTag.client'
import type {
  ProductRowControlsDelegateProps,
  ProductRowOnQuantityChangeInfos,
} from './_components/ProductRowControls.client'
import { ProductRowControls } from './_components/ProductRowControls.client'
import { ProductRowPrice } from './_components/ProductRowPrice.client'
import { ProductRowProductDetails } from './_components/ProductRowProductDetails.client'
import { ProductRowSkeletons } from './_components/ProductRowSkeletons'
import { ProductRowStocks } from './_components/ProductRowStocks.client'

gql(/* GraphQL */ `
  fragment FullPackaging on ProductPackaging {
    quantity
    unit
    incrementQuantity
    minimalSalesQuantity
  }

  fragment ProductRowBase_Product on Product {
    name
    type
    mainImage {
      ...FullPicture
    }
    brand {
      name
      urlKey
    }
    reference
    isLinkedProductsLinkVisible
    urlKey
  }

  fragment ProductRowPriceAndStock_Product on Product {
    reference
    ...FullProductAvailabilityItem_Availability
    price {
      discountPercentage
      discountExclTax
    }
    stock {
      packaging {
        ...FullPackaging
      }
    }
  }

  fragment ProductRow_Product on Product {
    ...ProductRowBase_Product
    ...ProductRowPriceAndStock_Product
  }

  query getLinkedProductsConfig {
    config {
      catalog {
        isLinkedProductEnabled
        g9LowStockThreshold
      }
    }
  }
`)

/**
 * Add relevant sources if necessary to avoid confusion in certain contexts
 */
export enum ProductRowSource {
  CART = 'cart',
  WISHLIST = 'wishlist',
  MINI_CART = 'minicart',
  ORDER_REQUEST = 'order-request',
}

export type ProductRowSourceProp = {
  source?: ProductRowSource
}

export type ProductRowPriceProps = {
  isLoadingPriceAndStocks?: boolean
  product: ProductRow_ProductFragment
  discount?: Maybe<{
    catalogDiscountAmount?: Maybe<number>
    percentage?: Maybe<number>
    webDiscount?: Maybe<{
      amount?: Maybe<number>
      hasCoupon?: Maybe<boolean>
      label?: Maybe<string>
    }>
    quantitySaleNextLevel?: Maybe<{
      gap?: Maybe<number>
      unitPrice?: Maybe<number>
    }>
  }>
  totalExclTax?: Maybe<number>
  unitPrice?: Maybe<{
    exclTax?: Maybe<number>
  }>
  displaySavedAmount?: boolean
  displayEquivalentProducts?: boolean
  displayPaymentWarning?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  displayReadOnlyQuantity?: boolean
  displayQuantityPicker?: boolean
  quantity: number
}

export type ProductRowPayloadProps = {
  id: string
  quantity: number
  quantityMax?: Maybe<number>
  deliveryDetails?: Maybe<
    ({
      availability: ShippingDelay
      date?: Maybe<string>
      realDeliveryDate?: Maybe<boolean>
      quantity: number
    } | null)[]
  >

  product: ProductRow_ProductFragment

  shouldLoadStocks?: boolean
  isClickAndCollectEnabled?: boolean

  promotionTag?: Maybe<{
    type?: Maybe<Tag>
    label?: Maybe<string>
  }>

  isInDrawer?: boolean
  displayClickAndCollect?: boolean
  displayAddToCartButton?: boolean
  displayQuantityPicker?: boolean
  displayLinkedProducts?: boolean
  displayStoreQuantity?: boolean
  displayRemainingStock?: boolean
  isLoading?: boolean
  isDisabled?: boolean

  onAddProductToCart?: (product: {
    sku: string
    quantity: number
    price: number
    clickAndCollectStatus?: ClickAndCollectStatus | null
  }) => Promise<void>
  onAddLinkedProductToCart?: (product: {
    sku: string
    quantity: number
    price: number
  }) => Promise<void>
  onAddProductToCartFailed?: () => void

  onLinkPress?: () => void | Promise<void>
}

export type ProductRowProps = ProductRowPayloadProps &
  ProductRowPriceProps &
  ProductRowControlsDelegateProps &
  ProductRowSourceProp

// eslint-disable-next-line sonarjs/cognitive-complexity
function ProductRowComponent({
  isInDrawer = false,
  displayClickAndCollect = false,
  displayPaymentWarning = false,
  displayAddToCartButton = false,
  discount: itemDiscount,
  promotionTag,
  id: itemId,
  quantity: itemQuantity,
  quantityMax: itemQuantityMax,
  deliveryDetails: itemDeliveryDetails,
  totalExclTax: itemTotalExclTax,
  unitPrice: itemUnitPrice,
  isClickAndCollectEnabled,
  shouldLoadStocks,
  product,
  isLoadingPriceAndStocks: isInitialPriceAndStocksLoading = false,
  isLoading = false,
  isDisabled = false,
  onAddProductToCart,
  onAddLinkedProductToCart,
  onAddProductToCartFailed,
  displayQuantityPicker = false,
  displayReadOnlyQuantity = false,
  displayEquivalentProducts = false,
  displayLinkedProducts = false,
  displayStoreQuantity = false,
  displayRemainingStock = true,
  displaySavedAmount = true,
  source,
  onLinkPress,
  ...controlsDelegates
}: ProductRowProps) {
  const { t, Trans } = useTranslation([
    'generic',
    'productRow',
    'checkout',
    'productAvailabilities',
  ])
  const isAuthenticated = useIsAuthenticated()
  const availabilityIssue = useMemo(
    () =>
      getAvailabilityIssue({
        // Cast needed: ProductRow_ProductFragment is a superset of AvailabilityProduct but TS can't infer the structural match
        product: product as Parameters<typeof getAvailabilityIssue>[0]['product'],
        isPublicPriceEnabled: source === ProductRowSource.WISHLIST,
        isProduct: false,
        isAuthenticated,
      }),
    [product, source, isAuthenticated],
  )

  const isInMiniCart = source === ProductRowSource.MINI_CART
  const isInWishlist = source === ProductRowSource.WISHLIST

  const { data: configData } = useQuery(GetLinkedProductsConfigDocument, APOLLO_CLIENT_SIDE_OPTIONS)

  const [isLocalLoadingStocks, startTransition] = useTransition()

  const onQuantityChange = useCallback(
    async (infos: ProductRowOnQuantityChangeInfos) => {
      startTransition(async () => {
        await controlsDelegates.onQuantityChange?.(infos)
      })
    },
    [controlsDelegates],
  )

  const packaging = product.stock?.packaging

  const nextLevelQuantity = getProductDisplayPrice(product).priceQuantity ?? packaging?.quantity

  const onPressAddToCart = useCallback(async () => {
    try {
      await onAddProductToCart?.({
        sku: product.reference,
        quantity: itemQuantity,
        price: itemUnitPrice?.exclTax || 0,
        clickAndCollectStatus: product.clickAndCollectStatus,
      })
    } catch {
      onAddProductToCartFailed?.()
    }
  }, [
    onAddProductToCart,
    product.reference,
    product.clickAndCollectStatus,
    itemQuantity,
    itemUnitPrice?.exclTax,
    onAddProductToCartFailed,
  ])

  if (!product) {
    console.warn('ProductRow: Product is missing')

    return null
  }

  const isNameValid = !!getSafeProductName(product.name)

  if (!itemId || !itemQuantity || !isNameValid) {
    const missingFields = [!itemId && 'id', !itemQuantity && 'qty', !isNameValid && 'product.name']

    console.warn(
      'ProductRow: Missing required fields in cartItem %O',
      missingFields.filter(Boolean).join(', '),
    )

    return null
  }

  const opacityClassName = isLoading && 'opacity-30'

  const isClickAndCollectOnly =
    product.clickAndCollectStatus === ClickAndCollectStatus.ClickAndCollectOnly
  const displayClickAndCollectLabel = displayClickAndCollect || isClickAndCollectOnly
  const clickAndCollectLabelI18nKey = isClickAndCollectOnly
    ? 'productAvailabilities:clickAndCollectOnly'
    : 'productRow:availableForClickAndCollect'

  const shouldDisplayControls =
    displayQuantityPicker ||
    displayReadOnlyQuantity ||
    itemDeliveryDetails?.length ||
    itemQuantityMax ||
    displayClickAndCollectLabel

  const g9LowStockThreshold = configData?.config?.catalog?.g9LowStockThreshold
    ? Number(configData.config.catalog.g9LowStockThreshold)
    : undefined

  const filteredItemDeliveryDetails = itemDeliveryDetails
    ?.filter(isItemDefined)
    ?.filter(({ availability, quantity }: { availability: ShippingDelay; quantity: number }) => {
      const isSplitDelivery = quantity < itemQuantity

      return (
        availability !== ShippingDelay.Unknown &&
        (!itemQuantityMax || itemQuantity < itemQuantityMax) &&
        (isSplitDelivery ||
          !g9LowStockThreshold ||
          (!!itemQuantityMax && itemQuantityMax <= g9LowStockThreshold))
      )
    })

  // isInitialPriceAndStocksLoading always shows skeleton even if availability data isn't ready yet
  // (isSellable=undefined would trigger availabilityIssue=IsNotSellable and suppress the skeleton)
  const isLoadingPriceAndStocks =
    isInitialPriceAndStocksLoading || (!availabilityIssue && isLocalLoadingStocks)

  const shouldDisplayRemainingStock =
    displayRemainingStock &&
    !isLoadingPriceAndStocks &&
    !availabilityIssue &&
    !shouldLoadStocks &&
    !displayStoreQuantity &&
    !!itemQuantityMax &&
    !!g9LowStockThreshold &&
    itemQuantityMax <= g9LowStockThreshold

  return (
    <fieldset className="relative flex flex-col gap-2 overflow-clip">
      <div
        className={twMerge(
          'z-10 sr-only:hidden absolute inset-0 z-[1] grid place-items-center transition-all duration-500',
          !isLoading && 'pointer-events-none opacity-0',
        )}
      >
        <Loader />
      </div>
      <div
        className={twMerge('flex flex-col gap-2 tablet:flex-row', opacityClassName)}
        data-testid="cart-item"
      >
        {/* Image */}
        <div className="flex w-full flex-col gap-1">
          {(source === ProductRowSource.WISHLIST || promotionTag?.type) &&
          isLoadingPriceAndStocks ? (
            <ProductRowSkeletons.Badge />
          ) : (
            !!promotionTag?.type && (
              <ProductTag tag={promotionTag.type} label={promotionTag.label ?? ''} />
            )
          )}
          <div className="flex gap-1 desktop:gap-3">
            <div className="shrink-0" data-testid="cart-item/picture">
              <Link
                variant="free"
                href={toProductPage(product.urlKey)}
                onPress={onLinkPress}
                intent={
                  product.urlKey ? createDisplayProductScreenIntent(product.urlKey) : undefined
                }
              >
                <CloudflarePictureProduct
                  className="size-[3.375rem] desktop:size-9"
                  src={product.mainImage?.set?.[0]?.url}
                  alt={product.mainImage?.alt || product.name || ''}
                  width={PRODUCT_IMAGE_SIZE_THUMBNAIL}
                />
              </Link>
            </div>
            {/* Content */}
            <div
              className={twMerge(
                'flex w-full gap-3 desktop-md:flex-row',
                !isInMiniCart && 'flex-col desktop-md:flex-row',
                isInMiniCart && 'flex-row',
              )}
            >
              <div
                className={twMerge(
                  'flex grow basis-0 gap-2 desktop:gap-3',
                  isInDrawer ? 'flex-col' : 'flex-wrap desktop:flex-row',
                )}
              >
                <ProductRowProductDetails
                  isInMiniCart={isInMiniCart}
                  isInDrawer={isInDrawer}
                  product={product}
                  onLinkPress={onLinkPress}
                />
                {/* Controls */}
                {shouldDisplayControls && !isInMiniCart && (
                  <div
                    className={twMerge(
                      'flex flex-col gap-1',
                      isInDrawer ? 'min-w-0' : 'desktop:w-[20rem] desktop-md:w-[17.5rem]',
                    )}
                  >
                    {displayQuantityPicker && !displayEquivalentProducts && (
                      <ProductRowControls
                        step={
                          product.stock?.packaging?.incrementQuantity ||
                          product.stock?.packaging?.quantity ||
                          1
                        }
                        itemQuantityMin={product.stock?.packaging?.minimalSalesQuantity}
                        itemQuantityMax={itemQuantityMax}
                        productName={product.name || ''}
                        isDisabled={isDisabled || isLoading}
                        // Avoid flashing the "unavailable" card while price/stock is still loading
                        // (isSellable=undefined would trigger availabilityIssue=IsNotSellable)
                        availabilityIssue={isLoadingPriceAndStocks ? null : availabilityIssue}
                        {...controlsDelegates}
                        itemQuantity={itemQuantity}
                        onQuantityChange={onQuantityChange}
                      />
                    )}

                    {isInWishlist && displayEquivalentProducts && (
                      <div className="flex min-w-22 items-center gap-2">
                        <QuantityPicker size="sm" value={itemQuantity} isDisabled />
                        <Button
                          size="md"
                          variant="tertiary"
                          isRound
                          iconLeft={<Icon.Trash2 className="text-brand-2-900" />}
                          onPress={controlsDelegates.onDelete}
                        >
                          {t('productRow:action.remove.label', { productName: product.name || '' })}
                        </Button>
                      </div>
                    )}

                    {displayReadOnlyQuantity && !displayQuantityPicker && !isInMiniCart && (
                      <QuantityPicker isReadOnly value={itemQuantity} />
                    )}

                    {isLoadingPriceAndStocks && <ProductRowSkeletons.Stocks />}

                    <div className="flex flex-col">
                      {!isLoadingPriceAndStocks &&
                        !availabilityIssue &&
                        !shouldDisplayRemainingStock && (
                          <ProductRowStocks
                            displayStoreQuantity={displayStoreQuantity}
                            filteredItemDeliveryDetails={filteredItemDeliveryDetails}
                            isClickAndCollectEnabled={isClickAndCollectEnabled ?? false}
                            product={product}
                            selectedQuantity={itemQuantity}
                            showAvailabilitiesCustomData={source !== ProductRowSource.WISHLIST}
                          />
                        )}
                      {/* TODO: This low-stock condition could be extracted into a utility function
                          (e.g. shouldDisplayLowStockBadge) to improve readability and testability. */}
                      {displayRemainingStock &&
                        !shouldLoadStocks &&
                        !displayStoreQuantity &&
                        itemQuantityMax &&
                        !displayClickAndCollect &&
                        g9LowStockThreshold &&
                        itemQuantityMax <= g9LowStockThreshold && (
                          <StatusLine variant="error">
                            <span className="tablet:whitespace-nowrap">
                              <Trans
                                i18nKey="productRow:remainingStock.remainingStock"
                                components={{
                                  medium: <span className="font-medium" />,
                                  bold: <span className="font-bold" />,
                                }}
                                values={{ count: itemQuantityMax }}
                              />
                            </span>
                          </StatusLine>
                        )}
                      {!isLoadingPriceAndStocks && displayClickAndCollectLabel && (
                        <StatusLine
                          variant="success"
                          className="max-w-[19rem] desktop-md:max-w-[17.5rem]"
                          aria-label={t('productRow:clickAndCollect')}
                        >
                          <span>
                            <Trans
                              i18nKey={clickAndCollectLabelI18nKey}
                              components={{
                                medium: <span className="font-medium" />,
                                bold: <span className="font-bold" />,
                              }}
                            />
                          </span>
                        </StatusLine>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <ProductRowPrice
                  source={source}
                  isLoadingPriceAndStocks={isLoadingPriceAndStocks}
                  product={product}
                  discount={itemDiscount}
                  totalExclTax={itemTotalExclTax}
                  unitPrice={itemUnitPrice}
                  displaySavedAmount={displaySavedAmount}
                  displayEquivalentProducts={displayEquivalentProducts}
                  displayPaymentWarning={displayPaymentWarning}
                  displayQuantityPicker={displayQuantityPicker}
                  displayReadOnlyQuantity={displayReadOnlyQuantity}
                  quantity={itemQuantity}
                  isLoading={isLoading}
                  isDisabled={isDisabled}
                />
                {source === ProductRowSource.WISHLIST &&
                  (isLoadingPriceAndStocks ? (
                    <div className="ml-auto desktop:self-center">
                      <ProductRowSkeletons.AddToCartButton />
                    </div>
                  ) : (
                    (!displayAddToCartButton || availabilityIssue) && (
                      <div className="ml-auto size-5 !rounded-none desktop:self-center" />
                    )
                  ))}
                {displayAddToCartButton && !availabilityIssue && !isLoadingPriceAndStocks && (
                  <div
                    className={twMerge(
                      'grow-1 ml-auto !rounded-none desktop:self-center',
                      promotionTag && 'flex flex-col justify-center',
                    )}
                    data-testid="product-row/add-to-cart"
                  >
                    <Button
                      size="md"
                      variant="primary"
                      isRound
                      iconLeft={<Icon.ShoppingBasket />}
                      onPress={onPressAddToCart}
                      intent={INTENT_SAME_AS_WEB}
                    >
                      {t('productRow:action.add.label', { productName: product.name })}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isLoadingPriceAndStocks &&
        !!itemDiscount?.quantitySaleNextLevel?.gap &&
        !!itemDiscount?.quantitySaleNextLevel?.unitPrice &&
        packaging && (
          // TODO: Replace with feature component
          <div
            className={twMerge(
              opacityClassName || undefined,
              'flex items-center justify-center gap-0.5 rounded-sm bg-error-bg px-2 py-1',
            )}
          >
            <Trans
              i18nKey="productRow:quantitySale"
              components={{
                gap: <strong />,
                discountedUnitPriceAndUnit: <span className="font-bold text-strawberry-red-600" />,
              }}
              values={{
                count: itemDiscount.quantitySaleNextLevel.gap,
                discountedUnitPrice: formatPrice(itemDiscount.quantitySaleNextLevel.unitPrice),
                unitQuantity: nextLevelQuantity,
                unit: t(`generic:units.${packaging.unit || Unit.P}`, {
                  count: nextLevelQuantity ?? 1,
                }),
              }}
            />
          </div>
        )}
      {product.urlKey &&
        displayLinkedProducts &&
        product.isLinkedProductsLinkVisible &&
        onAddLinkedProductToCart &&
        configData?.config?.catalog?.isLinkedProductEnabled && (
          <ProductRowLinkedProducts
            addProductToCartAction={onAddLinkedProductToCart}
            reference={product.reference}
            className={opacityClassName || undefined}
          />
        )}
    </fieldset>
  )
}

export const ProductRow = memo(ProductRowComponent)
